import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageLayout } from '../../components/organisms/PageLayout';
import { api } from '../../utils/api';
import { API_ENDPOINTS } from '../../config';
import { useUser } from '../../context/UserContext';
import { track } from '../../utils/analytics';
import "../../styles/welcome.css";

interface MeResponse {
  has_built_resume: boolean;
  has_ai_cv_review: boolean;
  has_generated_cover_letter: boolean;
  has_tracked_job_applications: boolean;
}

interface Kanban {
  uid: string;
}

interface CV {
  uid: string;
  title: string;
  updated_at?: string | null;
  created_at?: string | null;
}

type IntentChoice = 'has-cv' | 'no-cv';

const INTENT_STORAGE_KEY = 'onboarding_intent';

interface StepConfig {
  id: 'build-resume' | 'ai-review' | 'first-job' | 'ai-tools';
  index: number;
  title: string;
  desc: string;
  doneKey: keyof MeResponse;
  cta: string;
}

const STEPS: StepConfig[] = [
  {
    id: 'build-resume',
    index: 1,
    title: 'Build your resume',
    desc: 'Upload a PDF or start from scratch — takes under a minute.',
    doneKey: 'has_built_resume',
    cta: 'Start',
  },
  {
    id: 'first-job',
    index: 2,
    title: 'Save your first job',
    desc: 'Track applications, follow-ups and statuses in one board.',
    doneKey: 'has_tracked_job_applications',
    cta: 'Open Kanban',
  },
  {
    id: 'ai-review',
    index: 3,
    title: 'Get AI feedback on your resume',
    desc: 'See what to improve before you start applying.',
    doneKey: 'has_ai_cv_review',
    cta: 'Open AI Review',
  },
  {
    id: 'ai-tools',
    index: 4,
    title: 'Try AI tools',
    desc: 'Cover letters, interview prep and tailored suggestions.',
    doneKey: 'has_generated_cover_letter',
    cta: 'Open AI Tools',
  },
];

const Welcome: React.FC = () => {
  const { userProfile } = useUser();
  const navigate = useNavigate();
  const [me, setMe] = useState<MeResponse | null>(null);
  const [boardUid, setBoardUid] = useState<string | null>(null);
  const [intent, setIntent] = useState<IntentChoice | null>(
    () => (localStorage.getItem(INTENT_STORAGE_KEY) as IntentChoice | null) ?? null,
  );
  const [isLoading, setIsLoading] = useState(true);
  const [cvs, setCvs] = useState<CV[]>([]);
  const [cvPickerOpen, setCvPickerOpen] = useState(false);

  useEffect(() => {
    document.title = 'OfferFlow | Welcome';
  }, []);

  useEffect(() => {
    let cancelled = false;

    const fetchAll = async () => {
      try {
        const [meRes, kanbansRes, cvsRes] = await Promise.allSettled([
          api.get<MeResponse>(API_ENDPOINTS.me),
          api.get<Kanban[] | { kanbans: Kanban[] }>(API_ENDPOINTS.kanbans.list),
          api.get<CV[]>(API_ENDPOINTS.cvs.list),
        ]);

        if (cancelled) return;

        if (meRes.status === 'fulfilled' && meRes.value) {
          setMe(meRes.value);
        }

        if (kanbansRes.status === 'fulfilled' && kanbansRes.value) {
          const raw = kanbansRes.value;
          const list: Kanban[] = Array.isArray(raw)
            ? raw
            : (raw as { kanbans?: Kanban[] }).kanbans ?? [];
          if (list.length > 0) setBoardUid(list[0].uid);
        }

        if (cvsRes.status === 'fulfilled' && Array.isArray(cvsRes.value)) {
          setCvs(cvsRes.value);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    fetchAll();
    return () => { cancelled = true; };
  }, []);

  const welcomeTitle = useMemo(() => {
    if (userProfile?.first_name || userProfile?.last_name) {
      const name = [userProfile.first_name, userProfile.last_name].filter(Boolean).join(' ');
      return `Welcome, ${name}!`;
    }
    return 'Welcome!';
  }, [userProfile]);

  const completed = useMemo(() => {
    const set = new Set<string>();
    if (!me) return set;
    STEPS.forEach(step => {
      if (me[step.doneKey]) set.add(step.id);
    });
    return set;
  }, [me]);

  const isStepUnlocked = (stepId: StepConfig['id']): boolean => {
    switch (stepId) {
      case 'build-resume':
        return true;
      case 'ai-review':
      case 'first-job':
        return Boolean(me?.has_built_resume);
      case 'ai-tools':
        return Boolean(me?.has_tracked_job_applications);
      default:
        return true;
    }
  };

  const handleIntent = (choice: IntentChoice) => {
    setIntent(choice);
    localStorage.setItem(INTENT_STORAGE_KEY, choice);
    track('onboarding_intent_selected', { choice });

    if (choice === 'has-cv') {
      navigate('/resume-builder/new-base-resume?source=upload');
    } else {
      navigate('/resume-builder/new-base-resume?blank=1');
    }
  };

  const isDevHost = useMemo(() => {
    if (typeof window === 'undefined') return false;
    const host = window.location.hostname;
    return host === 'localhost' || host === '127.0.0.1' || host.startsWith('192.168.');
  }, []);

  const handleDevReset = async () => {
    if (!isDevHost) return;
    const fieldToType: Array<{ field: keyof MeResponse; type: string }> = [
      { field: 'has_built_resume', type: 'built_resume' },
      { field: 'has_ai_cv_review', type: 'ai_cv_review' },
      { field: 'has_generated_cover_letter', type: 'generated_cover_letter' },
      { field: 'has_tracked_job_applications', type: 'tracked_job_applications' },
    ];

    if (me) {
      await Promise.all(
        fieldToType
          .filter(({ field }) => me[field])
          .map(({ type }) => api.post(API_ENDPOINTS.checkMe, { type })),
      );
    }

    const uid = localStorage.getItem('user_uid');
    localStorage.removeItem(INTENT_STORAGE_KEY);
    localStorage.removeItem('onboarding_widget_dismissed');
    localStorage.removeItem('onboarding_widget_collapsed');
    if (uid) localStorage.removeItem(`upsell_popup_closed_at:${uid}`);

    track('onboarding_dev_reset');
    window.location.reload();
  };

  const handleCvPicked = (cvUid: string) => {
    setCvPickerOpen(false);
    track('onboarding_cv_picked_for_review', { cv_uid: cvUid });
    navigate(`/resume-builder/editor/${cvUid}?tab=ai-reviewer`);
  };

  const handleStepAction = (step: StepConfig) => {
    if (!isStepUnlocked(step.id)) return;
    track('onboarding_step_clicked', { step: step.id });

    switch (step.id) {
      case 'build-resume': {
        const target =
          intent === 'has-cv'
            ? '/resume-builder/new-base-resume?source=upload'
            : intent === 'no-cv'
              ? '/resume-builder/new-base-resume?blank=1'
              : '/resume-builder/new-base-resume';
        navigate(target);
        break;
      }
      case 'ai-review':
        if (cvs.length === 1) {
          navigate(`/resume-builder/editor/${cvs[0].uid}?tab=ai-reviewer`);
        } else {
          setCvPickerOpen(true);
        }
        break;
      case 'first-job':
        navigate(boardUid ? `/track/board/${boardUid}?onboarding=new-job` : '/dashboard');
        break;
      case 'ai-tools':
        navigate('/ai-tools');
        break;
    }
  };

  const breadcrumbItems = [
    { label: 'Home', href: '/' },
    { label: 'Welcome' },
  ];

  const showIntentCard = !isLoading && me !== null && !me.has_built_resume && intent === null;
  const totalSteps = STEPS.length;
  const doneSteps = completed.size;

  return (
    <PageLayout breadcrumbItems={breadcrumbItems}>
      <div className="welcome-wrapper">
        <div className="welcome-header">
          <h1 className="welcome-title">{welcomeTitle}</h1>
          <p className="welcome-sub">
            Get from zero to first interview in a few quick steps. Pick up wherever you left off.
          </p>
          {!isLoading && (
            <div className="welcome-progress">
              <div className="welcome-progress__track">
                <div
                  className="welcome-progress__fill"
                  style={{ width: `${(doneSteps / totalSteps) * 100}%` }}
                />
              </div>
              <div className="welcome-progress__label">
                {doneSteps} of {totalSteps} completed
              </div>
            </div>
          )}
        </div>

        {showIntentCard && (
          <div className="welcome-intent">
            <div className="welcome-intent__question">Do you have a current resume?</div>
            <div className="welcome-intent__options">
              <button
                type="button"
                className="welcome-intent__option"
                onClick={() => handleIntent('has-cv')}
              >
                <span className="welcome-intent__option-title">Yes, I'll upload it</span>
                <span className="welcome-intent__option-desc">
                  We'll parse it and pre-fill everything for you.
                </span>
              </button>
              <button
                type="button"
                className="welcome-intent__option"
                onClick={() => handleIntent('no-cv')}
              >
                <span className="welcome-intent__option-title">No, build from scratch</span>
                <span className="welcome-intent__option-desc">
                  Start with a blank resume in our editor.
                </span>
              </button>
            </div>
          </div>
        )}

        <div className="welcome-steps">
          {isLoading ? (
            <div className="welcome-loading">Loading…</div>
          ) : (
            STEPS.map(step => {
              const isDone = completed.has(step.id);
              const unlocked = isStepUnlocked(step.id);
              const stateClass = isDone
                ? 'welcome-step-card--done'
                : unlocked
                  ? 'welcome-step-card--active'
                  : 'welcome-step-card--locked';

              return (
                <div key={step.id} className={`welcome-step-card ${stateClass}`}>
                  <div className="welcome-step-card__index">
                    {isDone ? (
                      <span className="welcome-step-card__check" aria-label="Completed">✓</span>
                    ) : (
                      step.index
                    )}
                  </div>
                  <div className="welcome-step-card__body">
                    <div className="welcome-step-card__title">{step.title}</div>
                    <div className="welcome-step-card__desc">{step.desc}</div>
                  </div>
                  <div className="welcome-step-card__action">
                    {isDone ? (
                      <span className="welcome-step-card__status">Done</span>
                    ) : unlocked ? (
                      <button
                        type="button"
                        className="welcome-step-card__btn"
                        onClick={() => handleStepAction(step)}
                      >
                        {step.cta}
                      </button>
                    ) : (
                      <span className="welcome-step-card__status welcome-step-card__status--locked">
                        Locked
                      </span>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>

        {isDevHost && !isLoading && (
          <div className="welcome-dev">
            <button
              type="button"
              className="welcome-dev__btn"
              onClick={handleDevReset}
              title="localhost only — resets onboarding flags and local storage, then reloads."
            >
              Reset onboarding (dev)
            </button>
          </div>
        )}
      </div>

      {cvPickerOpen && (
        <div
          className="welcome-picker__overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="welcome-picker-title"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setCvPickerOpen(false);
          }}
        >
          <div className="welcome-picker">
            <div className="welcome-picker__header">
              <h2 id="welcome-picker-title" className="welcome-picker__title">
                Choose a resume to review
              </h2>
              <button
                type="button"
                className="welcome-picker__close"
                onClick={() => setCvPickerOpen(false)}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <p className="welcome-picker__hint">
              Pick the resume you want AI to analyze. We'll open it and switch to the AI Reviewer tab.
            </p>
            {cvs.length === 0 ? (
              <div className="welcome-picker__empty">
                No resumes yet. Build one first using step 1.
              </div>
            ) : (
              <ul className="welcome-picker__list">
                {cvs.map(cv => (
                  <li key={cv.uid}>
                    <button
                      type="button"
                      className="welcome-picker__item"
                      onClick={() => handleCvPicked(cv.uid)}
                    >
                      <span className="welcome-picker__item-title">{cv.title || 'Untitled resume'}</span>
                      {(cv.updated_at || cv.created_at) && (
                        <span className="welcome-picker__item-date">
                          Updated {new Date(cv.updated_at || cv.created_at || '').toLocaleDateString()}
                        </span>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </PageLayout>
  );
};

export default Welcome;
